// src/components/CartContext.jsx

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";

/* =========================================================
CONTEXT
========================================================= */

const CartContext =
  createContext();

/* =========================================================
PROVIDER
========================================================= */

export function CartProvider({
  children,
}) {

  const [cart, setCart] =
    useState([]);

  /* =========================================================
  LOAD CART
  ========================================================= */

  useEffect(() => {

    try {

      const saved =
        localStorage.getItem(
          "cart"
        );

      if (saved) {

        setCart(
          JSON.parse(saved)
        );
      }

    } catch (err) {

      console.error(
        "Failed to load cart:",
        err
      );

      localStorage.removeItem(
        "cart"
      );
    }

  }, []);

  /* =========================================================
  SAVE CART
  ========================================================= */

  useEffect(() => {

    localStorage.setItem(
      "cart",
      JSON.stringify(cart)
    );

  }, [cart]);

  /* =========================================================
  ADD TO CART
  ========================================================= */

  const addToCart = (
    product
  ) => {

    setCart((prev) => {

      /* SAME PRODUCT + SAME VARIANT */

      const exist = prev.find(

        (p) =>

          p._id ===
            product._id &&

          p.variant ===
            product.variant
      );

      /* UPDATE QTY */

      if (exist) {

        return prev.map(

          (p) =>

            p._id ===
              product._id &&

            p.variant ===
              product.variant

              ? {

                  ...p,

                  quantity:
                    p.quantity + 1,
                }

              : p
        );
      }

      /* NEW PRODUCT */

      return [

        ...prev,

        {

          ...product,

          quantity:1,

          addedAt:
            Date.now(),
        },
      ];
    });
  };

  /* =========================================================
  REMOVE FROM CART
  ========================================================= */

  const removeFromCart = (
    id,
    variant
  ) => {

    setCart((prev) =>

      prev.filter(

        (item) =>

          !(

            item._id === id &&

            item.variant ===
              variant
          )
      )
    );
  };

  /* =========================================================
  CLEAR CART
  ========================================================= */

  const clearCart = () => {

    setCart([]);

    localStorage.removeItem(
      "cart"
    );
  };

  /* =========================================================
  INCREMENT
  ========================================================= */

  const incrementQuantity = (
    id,
    variant
  ) => {

    setCart((prev) =>

      prev.map((p) =>

        p._id === id &&

        p.variant === variant

          ? {

              ...p,

              quantity:
                p.quantity + 1,
            }

          : p
      )
    );
  };

  /* =========================================================
  DECREMENT
  ========================================================= */

  const decrementQuantity = (
    id,
    variant
  ) => {

    setCart((prev) =>

      prev

        .map((p) =>

          p._id === id &&

          p.variant === variant

            ? {

                ...p,

                quantity:
                  p.quantity - 1,
              }

            : p
        )

        .filter(
          (p) =>
            p.quantity > 0
        )
    );
  };

  /* =========================================================
  SET QUANTITY
  ========================================================= */

  const setQuantity = (
    id,
    variant,
    qty
  ) => {

    if (qty <= 0) {

      removeFromCart(
        id,
        variant
      );

      return;
    }

    setCart((prev) =>

      prev.map((p) =>

        p._id === id &&

        p.variant === variant

          ? {
              ...p,
              quantity:qty,
            }

          : p
      )
    );
  };

  /* =========================================================
  COMPUTED VALUES
  ========================================================= */

  const cartCount =
    useMemo(

      () =>

        cart.reduce(

          (sum, item) =>

            sum +
            item.quantity,

          0
        ),

      [cart]
    );

  const cartSubtotal =
    useMemo(

      () =>

        cart.reduce(

          (sum, item) =>

            sum +

            item.price *
              item.quantity,

          0
        ),

      [cart]
    );

  /* GST */

  const tax =
    useMemo(

      () =>

        Number(
          (
            cartSubtotal *
            0.18
          ).toFixed(2)
        ),

      [cartSubtotal]
    );

  /* SHIPPING */

  const shipping =
    cartSubtotal > 500
      ? 0
      : 40;

  /* TOTAL */

  const cartTotal =
    cartSubtotal +
    tax +
    shipping;

  /* =========================================================
  PROVIDER
  ========================================================= */

  return (

    <CartContext.Provider
      value={{

        /* STATE */

        cart,

        /* ACTIONS */

        addToCart,

        removeFromCart,

        clearCart,

        incrementQuantity,

        decrementQuantity,

        setQuantity,

        /* COMPUTED */

        cartCount,

        cartSubtotal,

        tax,

        shipping,

        cartTotal,
      }}
    >

      {children}

    </CartContext.Provider>
  );
}

/* =========================================================
HOOK
========================================================= */

export const useCart = () =>
  useContext(CartContext);